import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { LogoutButton } from '../auth/LogoutButton';
import { useAuth } from '../auth/AuthContext';
import { kioskCheckIn } from './kiosk-api';
import './kiosk.css';

const FEEDBACK_MS = 3000;
const UNKNOWN_DNI = 'No encontramos ese DNI, consultá en recepción';

function messageForError(error) {
  if (error?.status === 404) return { text: UNKNOWN_DNI, kind: 'error' };
  if (error?.status === 429) return { text: 'Esperá un momento antes de volver a intentar.', kind: 'error' };
  if (error?.status === 400) return { text: 'Revisá el DNI e intentá nuevamente.', kind: 'error' };
  return { text: 'No se pudo conectar. Intentá de nuevo o consultá en recepción.', kind: 'error' };
}

export function KioskPage() {
  const { handleApiError, user } = useAuth();
  const [documentNumber, setDocumentNumber] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const feedbackTimer = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    inputRef.current?.focus();
    return () => {
      mounted.current = false;
      window.clearTimeout(feedbackTimer.current);
    };
  }, []);

  function showFeedback(nextFeedback) {
    window.clearTimeout(feedbackTimer.current);
    setFeedback(nextFeedback);
    feedbackTimer.current = window.setTimeout(() => {
      feedbackTimer.current = null;
      if (!mounted.current) return;
      setFeedback(null);
      inputRef.current?.focus();
    }, FEEDBACK_MS);
  }

  function cancelFeedbackForInteraction() {
    if (feedbackTimer.current === null) return;
    window.clearTimeout(feedbackTimer.current);
    feedbackTimer.current = null;
    setFeedback(null);
  }

  function appendDigit(digit) {
    cancelFeedbackForInteraction();
    setDocumentNumber((current) => `${current}${digit}`);
    setFeedback(null);
  }

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    cancelFeedbackForInteraction();
    setFeedback(null);
    setBusy(true);
    const submittedNumber = documentNumber;
    setDocumentNumber('');
    try {
      const result = await kioskCheckIn(submittedNumber);
      if (mounted.current) showFeedback({ text: result?.message || 'Asistencia registrada.', kind: 'success' });
    } catch (error) {
      const outcome = handleApiError(error);
      if (mounted.current && !outcome.handled) showFeedback(messageForError(error));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  return (
    <main
      className="gym-kiosk"
      onPointerDownCapture={cancelFeedbackForInteraction}
      onMouseDownCapture={cancelFeedbackForInteraction}
      onTouchStartCapture={cancelFeedbackForInteraction}
      onKeyDownCapture={cancelFeedbackForInteraction}
      onFocusCapture={cancelFeedbackForInteraction}
    >
      <header className="gym-kiosk__header"><Brand tone="dark" /></header>
      <section className="gym-kiosk__stage" aria-labelledby="gym-kiosk-title">
        <h1 id="gym-kiosk-title" className="gym-kiosk__title">Marcá tu asistencia</h1>
        <p className="gym-kiosk__instruction">Ingresá tu número de documento</p>
        <form className="gym-kiosk__form" onSubmit={submit}>
          <label className="gym-kiosk__label" htmlFor="gym-kiosk-document">Número de documento</label>
          <input
            ref={inputRef}
            id="gym-kiosk-document"
            className="gym-kiosk__input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            value={documentNumber}
            onChange={(event) => { cancelFeedbackForInteraction(); setDocumentNumber(event.target.value); setFeedback(null); }}
            onKeyDown={(event) => { if (event.key === 'Enter') submit(event); }}
            disabled={busy}
            aria-describedby="gym-kiosk-feedback"
          />
          <div className="gym-kiosk__keypad" aria-label="Teclado numérico">
            {'123456789'.split('').map((digit) => (
            <button key={digit} type="button" className="gym-kiosk__key" onClick={() => appendDigit(digit)} disabled={busy} aria-label={digit}>{digit}</button>
            ))}
            <button type="button" className="gym-kiosk__key gym-kiosk__key--utility" onClick={() => { cancelFeedbackForInteraction(); setDocumentNumber(''); }} disabled={busy} aria-label="Limpiar">Limpiar</button>
            <button type="button" className="gym-kiosk__key" onClick={() => appendDigit('0')} disabled={busy} aria-label="0">0</button>
            <button type="button" className="gym-kiosk__key gym-kiosk__key--utility" onClick={() => { cancelFeedbackForInteraction(); setDocumentNumber((current) => current.slice(0, -1)); }} disabled={busy} aria-label="Borrar último">⌫</button>
          </div>
          <button className="gym-kiosk__submit" type="submit" disabled={busy || !documentNumber.trim()}>
            {busy ? 'Registrando…' : 'Marcar asistencia'}
          </button>
        </form>
        <div id="gym-kiosk-feedback" className={`gym-kiosk__feedback${feedback ? ` gym-kiosk__feedback--${feedback.kind}` : ''}`} role={feedback?.kind === 'error' ? 'alert' : 'status'} aria-live="polite">
          {feedback?.text || ''}
        </div>
      </section>
      <footer className="gym-kiosk__footer"><span className="gym-kiosk__footer-brand">Estás en HAMMER</span><div className="gym-kiosk__footer-actions">{user?.role === 'admin' && <Link className="gym-button gym-button--secondary" to="/admin/kioscos">Volver a Kioscos</Link>}<LogoutButton variant="light" /></div></footer>
    </main>
  );
}
